import { useState, useEffect } from "react";
import { ChevronUp, Shield, Mail, ExternalLink } from "lucide-react";

const PrivacyPolicy = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="privacy-policy-page">
      <main className="pp-main" data-testid="privacy-policy-content">
        <article className="pp-article">
          {/* Header */}
          <header className="pp-header" data-testid="privacy-policy-header">
            <div className="pp-header-icon">
              <Shield size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Privacy Policy</h1>
            <p className="pp-app-name">Hearing Decibel Pro</p>
            <p className="pp-last-updated">Last updated: April 21, 2026</p>
          </header>

          {/* Summary */}
          <section id="summary" className="pp-section" data-testid="section-summary">
            <h2 className="pp-section-heading">Summary</h2>
            <div className="pp-highlight-box" data-testid="summary-highlight">
              <p>
                Hearing Decibel Pro is a <strong>local-first hearing health app</strong>. Your
                hearing tests, noise exposure data, and app settings stay on your device. We do
                not operate a server that stores your personal information, and we do not sell
                your data. We do use a small number of third-party services for purchase
                processing, anonymized analytics, and advertising attribution. This policy
                explains exactly what happens to data when you use the app.
              </p>
            </div>
          </section>

          {/* Who We Are */}
          <section id="who-we-are" className="pp-section" data-testid="section-who-we-are">
            <h2 className="pp-section-heading">Who We Are</h2>
            <p>
              This app is developed and published by <strong>Radhesh Gowd</strong> ("we," "our,"
              "us"), operating as an independent developer. You can contact us at{" "}
              <a href="mailto:support@radships.com" className="pp-link" data-testid="email-link-who">
                support@radships.com
              </a>{" "}
              with any questions about this policy or your data.
            </p>
          </section>

          {/* Information Collected */}
          <section
            id="information-collected"
            className="pp-section"
            data-testid="section-information-collected"
          >
            <h2 className="pp-section-heading">What Information We Collect and How It's Used</h2>

            {/* 1. Local Data */}
            <h3 className="pp-subsection-heading">1. Data Stored Only on Your Device</h3>
            <p>
              The following data is stored locally on your iPhone and never transmitted to us or
              to any server we control:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Hearing test results</strong> — your audiogram thresholds per frequency
                and per ear
              </li>
              <li>
                <strong>Noise exposure data</strong> — decibel readings, time-above-threshold
                statistics, daily dose calculations
              </li>
              <li>
                <strong>App settings and preferences</strong> — onboarding state, calibration
                offset, free test counter
              </li>
              <li>
                <strong>Feature usage flags</strong> — internal state tracking such as whether
                you have completed onboarding
              </li>
            </ul>
            <p>
              This data is stored in the app's local database (SwiftData) and in iOS
              UserDefaults. It is deleted when you uninstall the app or when you use the "Delete
              Account" option in Settings.
            </p>

            {/* 2. Microphone */}
            <h3 className="pp-subsection-heading">2. Microphone Audio</h3>
            <p>The app uses your device's microphone for two purposes:</p>
            <ul className="pp-list">
              <li>The decibel meter measures the ambient sound level in real time</li>
              <li>
                The hearing test requires a quiet environment, which the microphone helps verify
              </li>
            </ul>
            <p>
              Microphone audio is processed in real time and{" "}
              <strong>never recorded, stored, or transmitted</strong>. Only the resulting decibel
              values are retained on your device. The app does not listen when you are not
              actively using the meter or hearing test.
            </p>

            {/* 3. Subscriptions */}
            <h3 className="pp-subsection-heading">
              3. Subscription and Purchase Data (Processed by RevenueCat and Apple)
            </h3>
            <p>
              When you purchase a subscription or the Lifetime option, the purchase is processed
              by Apple's App Store and tracked by our subscription management partner,{" "}
              <strong>RevenueCat, Inc.</strong>
            </p>
            <ul className="pp-list">
              <li>
                Apple receives your purchase using your Apple ID and returns a receipt to the app
              </li>
              <li>
                RevenueCat receives the receipt and an anonymous identifier (not your Apple ID or
                email) to validate the purchase, track subscription status, and enable the
                "Restore Purchases" feature across your devices
              </li>
            </ul>
            <p>
              We use RevenueCat solely to manage in-app purchases. RevenueCat does not receive
              your hearing data, exposure data, microphone audio, or any other personal
              information beyond what is necessary for purchase validation. You can review
              RevenueCat's privacy practices at{" "}
              <a
                href="https://www.revenuecat.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="pp-link"
                data-testid="revenuecat-link"
              >
                revenuecat.com/privacy
                <ExternalLink size={12} className="pp-external-icon" />
              </a>
              .
            </p>

            {/* 4. Sign in with Apple */}
            <h3 className="pp-subsection-heading">4. Sign in with Apple (Optional)</h3>
            <p>
              If you choose to sign in with Apple, Apple provides the app with a stable anonymous
              user identifier (not your email address or name, unless you explicitly choose to
              share them). This identifier is stored only in your device's Keychain. It is used
              to:
            </p>
            <ul className="pp-list">
              <li>Associate your subscription with your Apple ID across devices</li>
              <li>
                Identify you if you reinstall the app on another device using the same Apple ID
              </li>
            </ul>
            <p>
              We do not collect your email address, even if you choose "Share My Email" during
              the Apple Sign In flow. If you choose to share your display name during sign-in,
              that name is stored only in your device's Keychain and is never transmitted off your
              device.
            </p>
            <p>
              You can sign out or delete your account at any time through Settings inside the
              app. Deletion removes all local data and the Keychain-stored identifier. Apple
              retains your Apple ID-level subscription independently — if you want to cancel a
              subscription, use Settings &gt; Apple ID &gt; Subscriptions on your device.
            </p>

            {/* 5. HealthKit */}
            <h3 className="pp-subsection-heading">5. Apple HealthKit (Optional)</h3>
            <p>
              If you grant HealthKit permission, the app can write the following to Apple Health:
            </p>
            <ul className="pp-list">
              <li>Hearing test audiogram results</li>
              <li>Environmental noise exposure readings</li>
            </ul>
            <p>
              The app writes only to HealthKit and does not read your Health data. HealthKit data
              remains under your control within the Apple Health app. You can revoke HealthKit
              access at any time via iOS Settings &gt; Privacy &amp; Security &gt; Health.
            </p>
            <p>
              If you do not grant HealthKit permission, the app still works normally — Health
              sync is optional.
            </p>

            {/* 6. Analytics and Advertising Attribution */}
            <h3 className="pp-subsection-heading">
              6. Analytics and Advertising Attribution (Google Analytics for Firebase)
            </h3>
            <p>
              To understand how users interact with the app and measure the effectiveness of
              advertising campaigns, we use <strong>Google Analytics for Firebase</strong>, a
              service provided by Google LLC.
            </p>
            <p>
              Firebase Analytics collects the following information automatically when you use the
              app:
            </p>
            <ul className="pp-list">
              <li>
                App lifecycle events (first open, session start, session end, app updates)
              </li>
              <li>
                Screen views and feature interactions (which tabs you visit, when you complete a
                hearing test, when you start or complete a purchase)
              </li>
              <li>
                Device information (iOS version, device model, app version, language, region)
              </li>
              <li>
                Device identifiers — Identifier for Vendor (IDFV) is always collected. Identifier
                for Advertisers (IDFA) is only collected if you grant tracking permission via
                Apple's App Tracking Transparency prompt
              </li>
              <li>
                Approximate location — Google derives coarse, IP-based location (typically city or
                region level) from network metadata. We do not collect precise GPS location
              </li>
            </ul>
            <p>
              Firebase Analytics data is processed by Google LLC and stored on Google's servers.
              We use this data to:
            </p>
            <ul className="pp-list">
              <li>Understand which app features are valued by users</li>
              <li>
                Measure the effectiveness of advertising campaigns and optimize ad spend (Google
                Ads ROAS reporting)
              </li>
              <li>Improve the app over time</li>
            </ul>
            <p>
              You can review Google's privacy practices at{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="pp-link"
                data-testid="google-privacy-link"
              >
                policies.google.com/privacy
                <ExternalLink size={12} className="pp-external-icon" />
              </a>
              .
            </p>

            {/* ATT */}
            <h4 className="pp-sub-subsection-heading">App Tracking Transparency (ATT)</h4>
            <p>
              After you complete onboarding, the app shows Apple's standard "Allow [App] to track
              your activity across other companies' apps and websites?" prompt. Your choice
              controls whether your IDFA is shared with Google for advertising attribution:
            </p>
            <ul className="pp-list">
              <li>
                <strong>If you choose Allow</strong>, your IDFA is included in Firebase Analytics
                events. This enables more precise advertising attribution and helps us optimize ad
                campaigns
              </li>
              <li>
                <strong>If you choose Ask App Not to Track</strong> (or never grant tracking),
                Firebase Analytics still functions but does not include your IDFA. Advertising
                attribution falls back to Apple's privacy-preserving SKAdNetwork system
              </li>
            </ul>
            <p>
              You can change your tracking choice at any time in iOS Settings &gt; Privacy &amp;
              Security &gt; Tracking.
            </p>

            {/* SKAdNetwork */}
            <h4 className="pp-sub-subsection-heading">Apple's SKAdNetwork (Always Active)</h4>
            <p>
              Independent of Firebase or your ATT choice, the app supports Apple's{" "}
              <strong>SKAdNetwork</strong> and <strong>AdAttributionKit</strong> frameworks. These
              are Apple-operated, privacy-preserving attribution systems built into iOS.
            </p>
            <p>
              When you install the app from an advertisement, Apple may send an anonymous,
              aggregated postback to the advertising network indicating that an install occurred.
              These postbacks:
            </p>
            <ul className="pp-list">
              <li>Contain no personal information about you</li>
              <li>
                Are aggregated at the campaign level, not the individual user level
              </li>
              <li>Are generated and mediated by Apple, not by us</li>
              <li>Do not track your behavior across apps or websites</li>
            </ul>
          </section>

          {/* What We Do Not Collect */}
          <section
            id="what-we-do-not-collect"
            className="pp-section"
            data-testid="section-what-we-do-not-collect"
          >
            <h2 className="pp-section-heading">What We Do Not Collect</h2>
            <p>For clarity, Hearing Decibel Pro does not collect, transmit, or share:</p>
            <ul className="pp-list pp-list-cross">
              <li>
                Your name, email address, phone number, or physical address (unless you
                explicitly choose to share your display name during Sign in with Apple, which is
                stored only locally in Keychain)
              </li>
              <li>Your precise GPS location</li>
              <li>
                Your hearing test results, noise exposure data, or microphone audio (all stored
                only on your device)
              </li>
              <li>Your contacts, photos, calendar, or other personal files</li>
              <li>
                Crash reports (we do not use Firebase Crashlytics or any crash reporting service)
              </li>
            </ul>
          </section>

          {/* How We Share */}
          <section id="how-we-share" className="pp-section" data-testid="section-how-we-share">
            <h2 className="pp-section-heading">How We Share Your Data</h2>
            <p>
              We share data only with the third parties listed below, and only in the limited
              manner described:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Apple</strong> — for App Store purchase processing, optional HealthKit
                data writes, optional Sign in with Apple authentication, and SKAdNetwork
                advertising attribution
              </li>
              <li>
                <strong>RevenueCat, Inc.</strong> — for subscription validation and management
              </li>
              <li>
                <strong>Google LLC (Google Analytics for Firebase)</strong> — for analytics and
                advertising attribution as described in Section 6
              </li>
            </ul>
            <p>
              We do not sell, rent, or trade your data to data brokers, marketing platforms,
              social media platforms, or any other third parties.
            </p>
          </section>

          {/* Data Retention */}
          <section
            id="data-retention"
            className="pp-section"
            data-testid="section-data-retention"
          >
            <h2 className="pp-section-heading">Data Retention</h2>
            <p>
              Because most data is stored on your device, you control most retention:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Uninstalling the app</strong> deletes all locally stored data
              </li>
              <li>
                <strong>"Delete Account" in Settings</strong> permanently deletes all hearing
                tests, exposure records, app settings, and signed-in state from your device
              </li>
              <li>
                <strong>Sign out</strong> clears your Apple ID association without deleting your
                data
              </li>
            </ul>
            <p>
              Subscription records held by Apple and RevenueCat for billing and fraud prevention
              purposes are retained according to those companies' respective policies. Firebase
              Analytics data is retained according to Google's data retention policies (default 14
              months for user-identifiable data; aggregated data may be retained longer). You can
              request deletion of your Firebase Analytics data by contacting us at{" "}
              <a href="mailto:support@radships.com" className="pp-link" data-testid="retention-email-link">
                support@radships.com
              </a>{" "}
              — we will forward your request to Google.
            </p>
          </section>

          {/* Children's Privacy */}
          <section id="children" className="pp-section" data-testid="section-children">
            <h2 className="pp-section-heading">Children's Privacy</h2>
            <p>
              Hearing Decibel Pro is not directed at children under the age of 13 (or the
              equivalent minimum age in your jurisdiction). We do not knowingly collect
              information from children. If you believe a child has used the app and data has
              somehow been collected, contact us and we will take appropriate action.
            </p>
          </section>

          {/* Medical Disclaimer */}
          <section
            id="medical-disclaimer"
            className="pp-section"
            data-testid="section-medical-disclaimer"
          >
            <h2 className="pp-section-heading">Medical Disclaimer</h2>
            <div className="pp-disclaimer-box" data-testid="medical-disclaimer-box">
              <p>
                Hearing Decibel Pro is a screening and educational tool, not a medical device.
                Hearing test results produced by the app are not a diagnosis and should not
                replace a clinical hearing evaluation performed by a licensed audiologist or
                healthcare provider. If you are concerned about your hearing, consult a qualified
                medical professional.
              </p>
            </div>
          </section>

          {/* Security */}
          <section id="security" className="pp-section" data-testid="section-security">
            <h2 className="pp-section-heading">Security</h2>
            <p>
              Data stored on your device is protected by iOS's built-in security mechanisms,
              including device-level encryption when your device passcode or biometric lock is
              enabled. Data transmitted between the app and our service providers (Apple,
              RevenueCat, Google) uses industry-standard HTTPS transport encryption.
            </p>
          </section>

          {/* International Users */}
          <section id="international" className="pp-section" data-testid="section-international">
            <h2 className="pp-section-heading">International Users</h2>
            <p>
              The app is available globally through the Apple App Store. Hearing test, exposure,
              and microphone data are processed locally on your device. Purchase data, analytics
              data, and Sign in with Apple data may be transferred to or stored in the United
              States or other countries where Apple, RevenueCat, and Google operate. Refer to
              their respective privacy policies for details.
            </p>
          </section>

          {/* Your Rights */}
          <section id="your-rights" className="pp-section" data-testid="section-your-rights">
            <h2 className="pp-section-heading">Your Rights</h2>
            <p>
              Depending on your jurisdiction (for example, if you are in the European Union,
              United Kingdom, or California), you may have rights regarding your personal data,
              including the right to access, correct, delete, or restrict its processing. To
              exercise these rights for data processed by us, contact{" "}
              <a href="mailto:support@radships.com" className="pp-link" data-testid="rights-email-link">
                support@radships.com
              </a>
              . For data processed by Apple, RevenueCat, or Google, please contact those
              companies directly using the privacy contacts in their respective policies.
            </p>
          </section>

          {/* Changes */}
          <section id="changes" className="pp-section" data-testid="section-changes">
            <h2 className="pp-section-heading">Changes to This Policy</h2>
            <p>
              We may update this policy occasionally to reflect changes in the app, in our
              practices, or in applicable requirements. When we do, we will update the "Last
              updated" date at the top. For significant changes, we will note the change
              prominently in the app or at this URL. Continued use of the app after a policy
              update constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section id="contact" className="pp-section" data-testid="section-contact">
            <h2 className="pp-section-heading">Contact</h2>
            <p>
              Questions, concerns, or requests regarding this policy or your data can be sent to:
            </p>
            <div className="pp-contact-card" data-testid="contact-card">
              <p className="pp-contact-name">Radhesh Gowd</p>
              <a
                href="mailto:support@radships.com"
                className="pp-contact-email"
                data-testid="contact-email-link"
              >
                <Mail size={16} />
                support@radships.com
              </a>
            </div>
          </section>

          {/* Footer */}
          <footer className="pp-footer" data-testid="privacy-policy-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {/* Back to top button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="back-to-top-button"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default PrivacyPolicy;
